import React, { useMemo, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  IconButton,
  Tooltip,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useHMG } from "../../../HMGContext";
import DateFormatter from "../../../hooks/DateFormatter";
import UndoLinesGrid from "./UndoLinesGrid";

/** Line items listed before the rest are hidden behind "See more". */
const COLLAPSED_ROWS = 4;

/** What the undo does, shown on hover of the info icon next to the title. */
const HELP_TEXT =
  "The line items you edited in this save go back to their values from before it, and the line items that depend " +
  "on them are recalculated. The result shows as unsaved changes: Save to keep it, or Discard to cancel. Only the " +
  "last save can be undone.";

/**
 * Confirmation dialog for "Undo last save" on a plan.
 * Shows who saved, when, and the grid of line items that go back ({@link UndoLinesGrid}: change type, changed
 * months, value now and after the undo). What the undo does (the result is unsaved changes) is in the info icon.
 *
 * @param {boolean} isOpen - Whether the dialog is open
 * @param {Function} setOpenUndoDialog - Function to close the dialog
 * @param {Function} onSubmit - Callback when the user confirms the undo
 * @param {string} planName - Plan version name shown in the title
 * @param {object} undoStatus - Response of GET /plan-details/undo/{planId}: { savedBy, savedOn, lines }
 */
const UndoLastSaveDialog = ({ isOpen, setOpenUndoDialog, onSubmit, planName, undoStatus }) => {
  const { setCurrentModalName } = useHMG();
  const [showAllLines, setShowAllLines] = useState(false);

  const lines = useMemo(() => undoStatus?.lines || [], [undoStatus]);
  const hasMore = lines.length > COLLAPSED_ROWS;
  const shownLines = useMemo(
    () => (showAllLines ? lines : lines.slice(0, COLLAPSED_ROWS)),
    [lines, showAllLines]
  );

  const handleClose = () => {
    setShowAllLines(false);
    setOpenUndoDialog(false);
    setCurrentModalName(null);
  };

  const handleSubmit = () => {
    if (onSubmit) onSubmit();
    handleClose();
  };

  return (
    <Dialog open={isOpen} fullWidth maxWidth="lg">
      <DialogTitle
        sx={{
          padding: "16px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontSize: "20px", fontWeight: 700, color: "#212B36", width: "98%", lineHeight: "30px" }}
        >
          Undo the last save in {planName}?
          <Tooltip title={HELP_TEXT} arrow placement="top">
            <InfoOutlinedIcon
              sx={{ fontSize: "18px", color: "#637381", marginLeft: "6px", cursor: "pointer", verticalAlign: "middle" }}
            />
          </Tooltip>
        </Typography>
        <IconButton onClick={handleClose}>
          <ClearIcon sx={{ width: "16px", height: "16px" }} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "16px" }}>
        <Typography sx={{ fontSize: "16px", lineHeight: "24px", fontWeight: 400 }}>
          Saved by <b>{undoStatus?.savedBy || "a user"}</b>
          {undoStatus?.savedOn && (
            <>
              {" on "}
              <DateFormatter value={undoStatus.savedOn} format="MMM d, yyyy h:mm a" />
            </>
          )}
          .
        </Typography>
        <UndoLinesGrid lines={shownLines} />
        {hasMore && (
          <Button
            size="small"
            onClick={() => setShowAllLines((shown) => !shown)}
            endIcon={showAllLines ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            sx={{ marginTop: "8px", fontWeight: 700, textTransform: "none", color: "#212B36" }}
          >
            {showAllLines ? "See less" : `See more (${lines.length - COLLAPSED_ROWS})`}
          </Button>
        )}
      </DialogContent>

      <DialogActions sx={{ display: "flex", justifyContent: "flex-end", padding: "16px" }}>
        <Button onClick={handleClose} variant="outlined" className="!font-bold !border-[#919EAB52]">
          No, keep it
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          sx={{ bgcolor: "#212B36", "&:hover": { backgroundColor: "#161C24", boxShadow: "none" } }}
        >
          Yes, undo last save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default UndoLastSaveDialog;
